import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { TeamPlanningReadOnly } from "./team-planning-readonly";

type HrWorker = {
  id: string;
  display_name: string;
  kind: "employee" | "contractor";
  email: string | null;
  phone: string | null;
  active: boolean;
  monthly_hours: number;
  work_days: number[];
  contract_title: string | null;
  contract_status: string | null;
  contract_starts_on: string | null;
  contract_ends_on: string | null;
  pending_document_count: number;
};

type HrLeave = {
  id: string;
  worker_id: string;
  worker_name: string;
  starts_on: string;
  ends_on: string;
  reason: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled";
  created_at: string;
};

type HrOverview = { workers: HrWorker[]; leave_requests: HrLeave[] };

const dateLabel = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("fr-FR", {
        timeZone: "Europe/Paris",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(`${value.slice(0, 10)}T12:00:00`))
    : "—";

const contractLabels: Record<string, string> = {
  draft: "Brouillon",
  sent: "Envoyé",
  signed: "Signé",
  active: "Actif",
  expired: "Expiré",
  terminated: "Terminé",
};

export function HrTeamWorkspace() {
  const [overview, setOverview] = useState<HrOverview>({ workers: [], leave_requests: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const load = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const result = await supabase.rpc("get_hr_workforce_overview");
    setLoading(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    const value = (result.data || {}) as Partial<HrOverview>;
    setOverview({
      workers: Array.isArray(value.workers) ? value.workers : [],
      leave_requests: Array.isArray(value.leave_requests) ? value.leave_requests : [],
    });
  }, []);
  useEffect(() => { void load(); }, [load]);
  const pendingLeaves = useMemo(
    () => overview.leave_requests.filter((item) => item.status === "pending"),
    [overview.leave_requests],
  );
  const reviewLeave = async (leaveId: string, status: "approved" | "rejected") => {
    if (!supabase) return;
    setBusyId(leaveId);
    setMessage("");
    const result = await supabase.rpc("review_hr_leave_request", {
      p_leave_id: leaveId,
      p_status: status,
    });
    setBusyId("");
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    await load();
    setMessage(status === "approved" ? "Congé approuvé et historisé." : "Congé refusé et historisé.");
  };
  return (
    <section className="hr-delegated-workspace" data-testid="hr-team-workspace">
      <header className="panel admin-hr-workspace-head">
        <div>
          <span className="eyebrow">Responsabilité RH</span>
          <h1>Équipe &amp; RH</h1>
          <p>Suivez les personnes, contrats, documents et congés. Les salaires, coûts employeur et arbitrages de direction ne sont pas exposés par ce profil.</p>
        </div>
        <span className="status-badge success">Droits RH délégués</span>
      </header>
      {message && <p className={`notice ${/historisé/.test(message) ? "success" : "error"}`}>{message}</p>}
      {loading ? <p>Chargement du dossier RH sécurisé…</p> : <>
        <section className="panel hr-delegated-section">
          <div className="section-heading">
            <div><span className="eyebrow">Annuaire</span><h2>Situation administrative de l’équipe</h2></div>
            <strong>{overview.workers.filter((item) => item.active).length} actif(s)</strong>
          </div>
          <div className="hr-delegated-directory">
            {overview.workers.map((worker) => (
              <article key={worker.id}>
                <header><div><strong>{worker.display_name}</strong><small>{worker.kind === "employee" ? "Employé" : "Sous-traitant"}</small></div><span className={`status-badge ${worker.active ? "success" : "danger"}`}>{worker.active ? "Actif" : "Archivé"}</span></header>
                <p>{worker.email || "E-mail à compléter"}{worker.phone ? ` · ${worker.phone}` : ""}</p>
                <dl>
                  <div><dt>Contrat</dt><dd>{worker.contract_title || "À préparer"}</dd></div>
                  <div><dt>État</dt><dd>{worker.contract_status ? contractLabels[worker.contract_status] || worker.contract_status : "Absent"}</dd></div>
                  <div><dt>Période</dt><dd>{worker.contract_starts_on ? `${dateLabel(worker.contract_starts_on)} → ${dateLabel(worker.contract_ends_on)}` : "Non renseignée"}</dd></div>
                  <div><dt>Documents</dt><dd>{worker.pending_document_count ? `${worker.pending_document_count} à traiter` : "À jour"}</dd></div>
                </dl>
              </article>
            ))}
            {!overview.workers.length && <p className="empty">Aucune personne accessible.</p>}
          </div>
        </section>
        <section className="panel hr-delegated-section" data-testid="hr-leave-review">
          <div className="section-heading">
            <div><span className="eyebrow">Décisions à prendre</span><h2>Demandes de congés</h2></div>
            <strong>{pendingLeaves.length} en attente</strong>
          </div>
          <div className="hr-delegated-leaves">
            {pendingLeaves.map((leave) => (
              <article key={leave.id}>
                <div><strong>{leave.worker_name}</strong><span>{dateLabel(leave.starts_on)} → {dateLabel(leave.ends_on)}</span><small>{leave.reason || "Aucun motif renseigné"}</small></div>
                <div className="row-actions"><button type="button" disabled={busyId === leave.id} onClick={() => void reviewLeave(leave.id, "approved")}>Approuver</button><button type="button" className="danger" disabled={busyId === leave.id} onClick={() => void reviewLeave(leave.id, "rejected")}>Refuser</button></div>
              </article>
            ))}
            {!pendingLeaves.length && <p className="empty">Aucune demande de congé en attente.</p>}
          </div>
        </section>
      </>}
      <TeamPlanningReadOnly />
    </section>
  );
}
